import { useState } from 'react';

/**
 * Custom hook for number inputs that replaces value on first keystroke
 * Tracks "pristine" state - when focused but not yet modified
 *
 * @returns {Object} - Hook handlers and state
 * @returns {boolean} isPristine - Whether input is in pristine state (focused, not modified)
 * @returns {Function} handleFocus - Focus event handler
 * @returns {Function} handleKeyDown - KeyDown event handler that intercepts first digit
 * @returns {Function} handleBlur - Blur event handler
 *
 * @example
 * const { isPristine, handleFocus, handleKeyDown, handleBlur } = useReplaceOnFirstType();
 *
 * <input
 *   value={value}
 *   onChange={onChange}
 *   onFocus={handleFocus}
 *   onKeyDown={(e) => handleKeyDown(e, value, (newValue) => onChange({ target: { value: newValue } }))}
 *   onBlur={handleBlur}
 *   className={isPristine ? 'input-pristine' : ''}
 * />
 */
export const useReplaceOnFirstType = () => {
  const [isPristine, setIsPristine] = useState(false);

  /**
   * Marks the input pristine so the next digit replaces the value.
   *
   * @returns {void}
   */
  const handleFocus = () => {
    setIsPristine(true);
  };

  /**
   * On first digit/decimal while pristine, replaces the current value.
   *
   * @param {React.KeyboardEvent} e Keydown event.
   * @param {*} currentValue Current input value (unused).
   * @param {Function} setValue Setter for the replaced character.
   * @returns {void}
   */
  const handleKeyDown = (e, currentValue, setValue) => {
    // Check if input is pristine and user pressed a digit or decimal point
    if (isPristine && /^[0-9.]$/.test(e.key)) {
      e.preventDefault();
      setValue(e.key);
      setIsPristine(false);
    } else if (isPristine && e.key !== 'Tab') {
      // Any other key (except Tab) exits pristine mode
      setIsPristine(false);
    }
  };

  /**
   * Clears the pristine flag on blur.
   *
   * @returns {void}
   */
  const handleBlur = () => {
    setIsPristine(false);
  };

  return {
    isPristine,
    handleFocus,
    handleKeyDown,
    handleBlur,
  };
};
